/**
 * Seasonal, code-only display override for subscription products.
 *
 * On the public sales pages the subscriptions are presented under seasonal
 * names ("Whee! Fast" → "Whee! Året rundt", "Whee! Fleks" → "Whee! Sommersesong"). This is
 * intentionally NOT changed in Airtable or Stripe:
 *   - Airtable keeps the original `displayName` (aligned with Stripe, and so
 *     Make.com automations matching on the name string are unaffected).
 *   - Stripe keeps its product/price unchanged.
 *
 * The override is keyed on the original Airtable `displayName`, so it is applied
 * only where subscription data flows through the sales data layer
 * (`getSubscriptions` / `getSubscriptionProductsByBikeModelId`). The min-side /
 * "mitt abonnement" pages read the order snapshot (`subscriptionsName`) instead
 * and are therefore left showing the original names.
 *
 * To revert after the season, remove the entries from `SEASONAL_SALES_OVERRIDES`.
 */

type SeasonalOverride = {
  name: string;
  // Optional — omit to override only the display name (sales points / binding text unchanged).
  salesPoints?: string[];
  condition?: string;
};

export const SEASONAL_SALES_OVERRIDES: Record<string, SeasonalOverride> = {
  "Whee! Fast": {
    name: "Whee! Året rundt"
  },
  "Whee! Fleks": {
    name: "Whee! Sommersesong",
    // Full-array override (predictable; not dependent on matching live Airtable strings).
    salesPoints: ["Ingen binding", "Bindingstid → 31. oktober", "Frihet til å velge"],
    condition: "Bindingstid → 31. oktober"
  }
};

/** Returns the seasonal display name, or the original name when no override exists. */
export function overrideName(name: string): string {
  return SEASONAL_SALES_OVERRIDES[name]?.name ?? name;
}

/**
 * Returns the seasonal sales points, or the provided fallback when no override exists.
 * Generic so the caller's array type (e.g. a non-empty tuple) is preserved.
 */
export function overrideSalesPoints<T extends string[]>(name: string, fallback: T): T {
  return (SEASONAL_SALES_OVERRIDES[name]?.salesPoints as T | undefined) ?? fallback;
}

/** Returns the seasonal binding condition, or the provided fallback when no override exists. */
export function overrideCondition(name: string, fallback: string): string {
  return SEASONAL_SALES_OVERRIDES[name]?.condition ?? fallback;
}
